import { useEffect, useState, useMemo, useCallback } from 'react'
import { Coffee, RefreshCw } from 'lucide-react'
import clsx from 'clsx'
import { useAuth } from '@/contexts/AuthContext'
import { tumPersonelleriGetir } from '@/lib/services/personel-service'
import { tumBugunkuMolalar, MOLA_HAKKI_DAKIKA, type Mola } from '@/lib/services/mola-service'
import type { Vardiyali, KullaniciPersonel } from '@/types'

export function YoneticiMolalarPage() {
  const { kullanici } = useAuth()

  const benimSubeIdleri = useMemo<string[] | null>(() => {
    if (kullanici?.rol === 'boss') return null
    if (kullanici?.rol !== 'personel') return []
    return (kullanici as KullaniciPersonel).subeIdleri ?? []
  }, [kullanici])

  const [personeller, setPersoneller] = useState<Vardiyali[]>([])
  const [molalar, setMolalar] = useState<Mola[]>([])
  const [yukleniyor, setYukleniyor] = useState(true)
  const [yenileniyor, setYenileniyor] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  // Canlı sayaç için
  const [, setTick] = useState(0)
  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 1000)
    return () => clearInterval(t)
  }, [])

  const yukle = useCallback(async (sessiz = false) => {
    if (!sessiz) setYukleniyor(true)
    setYenileniyor(true)
    try {
      const [p, m] = await Promise.all([
        tumPersonelleriGetir(),
        tumBugunkuMolalar(),
      ])

      const aktifP = p.filter(x => x.aktif)
      const benimP = benimSubeIdleri === null
        ? aktifP
        : aktifP.filter(x =>
            (x.subeIdleri ?? []).some(id => benimSubeIdleri.includes(id))
          )
      const benimPIds = new Set(benimP.map(x => x.id))
      const benimM = m.filter(mo => benimPIds.has(mo.personelId))

      setPersoneller(benimP)
      setMolalar(benimM)
      setHata(null)
    } catch (err) {
      console.error(err)
      setHata('Molalar yüklenemedi.')
    } finally {
      setYukleniyor(false)
      setYenileniyor(false)
    }
  }, [benimSubeIdleri])

  useEffect(() => { yukle() }, [yukle])

  // 30 saniyede otomatik tazele
  useEffect(() => {
    const i = setInterval(() => yukle(true), 30000)
    return () => clearInterval(i)
  }, [yukle])

  const personelHaritasi = useMemo(() => {
    const m = new Map<string, Vardiyali>()
    personeller.forEach(p => m.set(p.id, p))
    return m
  }, [personeller])

  // Personel bazlı bugünkü toplam dakika ve aktif mola
  const personelMolaOzet = useMemo(() => {
    const m = new Map<string, { toplamDk: number; aktif: Mola | null; sayisi: number }>()
    for (const mola of molalar) {
      const mevcut = m.get(mola.personelId) ?? { toplamDk: 0, aktif: null, sayisi: 0 }
      mevcut.sayisi += 1
      if (!mola.bitis) {
        mevcut.aktif = mola
        mevcut.toplamDk += Math.floor((Date.now() - mola.baslangic.toMillis()) / 60000)
      } else {
        mevcut.toplamDk += Math.floor(
          (mola.bitis.toMillis() - mola.baslangic.toMillis()) / 60000
        )
      }
      m.set(mola.personelId, mevcut)
    }
    return m
  }, [molalar])

  const aktifSayisi = molalar.filter(m => !m.bitis).length

  if (yukleniyor) {
    return (
      <div className="card text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mb-3"></div>
        <p className="text-gray-600">Yükleniyor...</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Coffee className="w-6 h-6 text-brand-600" />
          <h1 className="text-2xl font-bold text-gray-900">Molalar</h1>
          <span className="text-sm text-gray-500">
            ({aktifSayisi} aktif / {molalar.length} bugün)
          </span>
        </div>
        <button
          onClick={() => yukle()}
          disabled={yenileniyor}
          className="text-sm text-brand-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${yenileniyor ? 'animate-spin' : ''}`} />
          Yenile
        </button>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-3 text-sm">
          {hata}
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
        Mola hakkı: <b>{MOLA_HAKKI_DAKIKA} dakika/gün</b>. Aşan dakikalar maaştan düşülür.
        Bu sayfa görüntüleme amaçlıdır.
      </div>

      {/* Aktif molalar */}
      <div>
        <h2 className="text-sm font-bold text-gray-700 uppercase mb-2">Şu An Molada</h2>
        {molalar.filter(m => !m.bitis).length === 0 ? (
          <div className="card text-center py-6 text-gray-500 text-sm">
            Şu an molada kimse yok.
          </div>
        ) : (
          <div className="grid gap-2">
            {molalar.filter(m => !m.bitis).map(m => {
              const p = personelHaritasi.get(m.personelId)
              const gecenDk = Math.floor((Date.now() - m.baslangic.toMillis()) / 60000)
              const asim = Math.max(0, gecenDk - MOLA_HAKKI_DAKIKA)
              return (
                <div
                  key={m.id}
                  className={clsx(
                    'card p-3 border-l-4',
                    asim > 0 ? 'border-l-red-500 bg-red-50/30' : 'border-l-amber-500 bg-amber-50/30'
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {p?.isim ?? 'Bilinmeyen'}
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Başlangıç:{' '}
                        {m.baslangic.toDate().toLocaleTimeString('tr-TR', {
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={clsx(
                        'text-lg font-bold tabular-nums',
                        asim > 0 ? 'text-red-700' : 'text-amber-700'
                      )}>
                        {gecenDk} dk
                      </p>
                      {asim > 0 && (
                        <p className="text-[10px] font-bold text-red-700">
                          AŞIM +{asim}dk
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Personel bazlı bugünkü özet */}
      <div>
        <h2 className="text-sm font-bold text-gray-700 uppercase mb-2">
          Bugünkü Personel Özeti
        </h2>
        {personelMolaOzet.size === 0 ? (
          <div className="card text-center py-6 text-gray-500 text-sm">
            Bugün henüz mola olmamış.
          </div>
        ) : (
          <div className="card divide-y divide-gray-100">
            {Array.from(personelMolaOzet.entries())
              .sort((a, b) => b[1].toplamDk - a[1].toplamDk)
              .map(([personelId, ozet]) => {
                const p = personelHaritasi.get(personelId)
                const asim = Math.max(0, ozet.toplamDk - MOLA_HAKKI_DAKIKA)
                return (
                  <div
                    key={personelId}
                    className="py-2.5 first:pt-0 last:pb-0 flex items-center justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {p?.isim ?? 'Bilinmeyen'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {ozet.sayisi} mola
                        {ozet.aktif && (
                          <span className="ml-2 inline-flex items-center gap-1 text-amber-700">
                            <Coffee className="w-3 h-3" />
                            Şu an molada
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={clsx(
                        'text-base font-bold tabular-nums',
                        asim > 0 ? 'text-red-700' : 'text-gray-900'
                      )}>
                        {ozet.toplamDk} dk
                      </p>
                      {asim > 0 ? (
                        <p className="text-[10px] font-bold text-red-700">
                          AŞIM +{asim}dk
                        </p>
                      ) : (
                        <p className="text-[10px] text-gray-500">
                          / {MOLA_HAKKI_DAKIKA} dk
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
          </div>
        )}
      </div>
    </div>
  )
}
